import React, { useEffect, useState } from 'react';
import { Button, CardBody, CardHeader, Row, Col } from 'reactstrap';
import { useStateSelector } from '../../../../../store/selectors';
import './ProfilePreview.scss';
import { BuyerUploads } from '../BuyerUploads/BuyerUploads';
import { MarketplaceBuyerUploadAccessLevel } from '../../../../../slices/marketplace/buyer/models/buyer-upload';
import Helpers from '../../../../../utils/helper';
import { NdaTermPreferences } from '../PublicProfile/NdaTermPreferences/NdaTermPreferences';
import { Form } from '@availity/form';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { useServiceFocuses } from '../../../../../custom-hooks/useServiceFocuses';
import { mapServiceFocusesToOptions } from '../../../../UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { useFeatureFlag } from '../../../../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../../../../FeatureFlags/FeatureFlags.enum';
import { LegalIssuesForm } from '../../../../../components/LoiForm/LegalIssuesForm';
import { LoiFormMode } from '../../../../../slices/marketplace/loi-form/loi-form-mode.enum';

export interface ProfilePreviewProps {
    isReadonly: boolean;
    setIsEditMode?: (isEditMode: boolean) => void;
}

export const ProfilePreview = (props: ProfilePreviewProps) => {
    const isFF_MrktplcBuyers_3_13Enabled = useFeatureFlag(FeatureFlagsEnum.FF_MrktplcBuyers_3_13);
    const serviceFocuses = useServiceFocuses();
    const targetBuyer = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    const [formValues, setFormValues] = useState({});
    const formatList = (items: string[]): string => {
        if (!items || items.length === 0) return '';
        if (items.length === 1) return items[0];
        if (items.length === 2) return `${items[0]} and ${items[1]}`;
        return `${items.slice(0, -1).join(', ')} and ${
            items[items.length - 1]
        }`;
    };

    const loiForm = targetBuyer?.profile?.loiForm;
    const legalIssues = loiForm?.legalIssues ?? [];
    const additionalCovenants = loiForm?.additionalCovenants ?? [];
    const legalIssueSections = loiForm?.legalIssueSections ?? [];

    const legalIssueSectionsFiltered = legalIssueSections.filter(
        (s) => legalIssues.some((li) => li.sectionId === s.id)
    );
    const additionalCovenantSections = legalIssueSections.filter(
        (s) => additionalCovenants.some((ac) => ac.sectionId === s.id)
    );

    const isUploadsExist = (accessLevel: MarketplaceBuyerUploadAccessLevel) => {
        return (
            targetBuyer?.profile?.uploads?.filter(
                (x) => x.accessLevel === accessLevel
            ).length > 0
        );
    };

    useEffect(() => {
        if (targetBuyer?.profile?.currentNdaTermPreferences) {
            const ndaTermPreferencesState =
                targetBuyer?.profile?.currentNdaTermPreferences;
            setFormValues({
                nonDisclosurePeriod:
                    ndaTermPreferencesState?.nonDisclosurePeriod ?? '',
                nonSolicitationDvmNamesVisible:
                    ndaTermPreferencesState?.nonSolicitationDvmNamesVisible ??
                    '',
                nonSolicitationDvmNamesBlinded:
                    ndaTermPreferencesState?.nonSolicitationDvmNamesBlinded ??
                    '',
                nonSolicitationStaffNamesVisible:
                    ndaTermPreferencesState?.nonSolicitationStaffNamesVisible ??
                    '',
                nonSolicitationStaffNamesBlinded:
                    ndaTermPreferencesState?.nonSolicitationStaffNamesBlinded ??
                    '',
            });
        }
    }, [targetBuyer?.profile?.currentNdaTermPreferences]);

    return (
        <div className="buyer-profile profile-preview">
            {props.isReadonly ? (
                <></>
            ) : (
                <CardHeader className="profile-preview-header space-between">
                    Buyer Profile
                    <Button
                        color="primary"
                        className="btn"
                        onClick={() => {
                            props.setIsEditMode(true);
                        }}>
                        Edit Profile
                    </Button>
                </CardHeader>
            )}

            <CardBody className="card-body-custom">
                <Row>
                    <Col md={4} className="left-panel">
                        <div className="buyer-info">
                            {' '}
                            <div className="general-information">
                                <div className="logo-preview">
                                    <img
                                        src={
                                            targetBuyer?.profile?.logoUrl
                                        }></img>
                                </div>

                                <div className="section-details">
                                    <div className="section-label">
                                        Buyer Name:
                                    </div>
                                    <div className="section-value">
                                        {targetBuyer?.profile?.companyName}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Buyer Website:
                                    </div>
                                    <div className="section-value">
                                        {targetBuyer?.profile?.website ? (
                                            <a
                                                href={
                                                    targetBuyer?.profile?.website.startsWith(
                                                        'http'
                                                    )
                                                        ? targetBuyer?.profile
                                                              ?.website
                                                        : `https://${targetBuyer?.profile?.website}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {targetBuyer?.profile?.website}
                                            </a>
                                        ) : (
                                            'N/A'
                                        )}
                                    </div>
                                </div>

                                <div className="section-details column">
                                    <div className="section-label">
                                        Description:
                                    </div>
                                    <div className="section-value">
                                        {
                                            targetBuyer?.profile
                                                ?.companyDescription
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="location-information">
                                <div className="section-details">
                                    <div className="section-label">
                                        Acquision Focus:
                                    </div>
                                </div>
                                <div className="section-details column">
                                    <div className="section-label">
                                        Geographic Focus:
                                    </div>

                                    <div className="section-value">
                                        Region:{' '}
                                        {formatList(
                                            targetBuyer?.profile
                                                ?.stateAcquisitionRegion
                                        )}
                                    </div>
                                    <div className="section-value">
                                        Location Type:{' '}
                                        {formatList(
                                            targetBuyer?.profile
                                                ?.geographyMarket
                                        )}
                                    </div>
                                </div>
                                <div className="section-details practice-size-section column">
                                    <div className="section-label">
                                        Practice Size: Min / "Sweet Spot" / Max
                                    </div>

                                    <div className="section-value">
                                        Revenue:{' '}
                                        {Helpers.currencyFormat(
                                            targetBuyer?.profile
                                                ?.targetPracticeSizeRevenueMin,
                                            false
                                        )}{' '}
                                        /{' '}
                                        {Helpers.currencyFormat(
                                            targetBuyer?.profile
                                                ?.targetPracticeSizeRevenueSweetspot,
                                            false
                                        )}{' '}
                                        /{' '}
                                        {Helpers.currencyFormat(
                                            targetBuyer?.profile
                                                ?.targetPracticeSizeRevenueMax,
                                            false
                                        )}
                                    </div>
                                    <div className="section-value">
                                        FTE DVMs:{' '}
                                        {
                                            targetBuyer?.profile
                                                ?.targetPracticeSizeFteMin
                                        }{' '}
                                        /{' '}
                                        {
                                            targetBuyer?.profile
                                                ?.targetPracticeSizeFteSweetspot
                                        }{' '}
                                        /{' '}
                                        {
                                            targetBuyer?.profile
                                                ?.targetPracticeSizeFteMax
                                        }
                                    </div>
                                </div>
                                {targetBuyer?.profile?.practiceFocuses?.length >
                                0 ? (
                                    <div className="section-details column">
                                        <div className="section-label">
                                            Service Focus:
                                        </div>
                                        <div className="section-value">
                                            {serviceFocuses.filter(
                                                (mapping) =>
                                                    (
                                                        targetBuyer?.profile
                                                            ?.practiceFocuses ??
                                                        []
                                                    ).some(
                                                        (buyerOption) =>
                                                            buyerOption ==
                                                            mapping.id
                                                    )
                                            ).map(
                                                (focusMapping, index, arr) => (
                                                    <span
                                                        key={index}
                                                        className="practice-focus">
                                                        {
                                                            mapServiceFocusesToOptions(
                                                                serviceFocuses
                                                            )[focusMapping.id]
                                                        }
                                                        {index < arr.length - 1
                                                            ? ', '
                                                            : ''}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    []
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col md={8} className="right-panel">
                        <div className="historical-map-preview">
                            <img src={targetBuyer?.profile?.growthMapUrl}></img>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <CardHeader className="buyer-uploads-header space-between">
                            Data NDA Terms Preferences
                        </CardHeader>
                        <CardBody className="nda-term-preferences-section">
                            {targetBuyer?.profile
                                ?.currentNdaTermPreferences && (
                                <Form
                                    initialValues={formValues}
                                    enableReinitialize={true}
                                    onSubmit={() => {}}>
                                    <NdaTermPreferences
                                        isFullScreen={false}
                                        isSaving={false}
                                        isReadonly={true}
                                    />
                                </Form>
                            )}
                        </CardBody>
                    </Col>
                </Row>

                {isFF_MrktplcBuyers_3_13Enabled && legalIssues.length > 0 && (
                    <Row>
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header space-between">
                                Legal Issues
                            </CardHeader>
                            <CardBody>
                                <LegalIssuesForm
                                    legalIssueSections={legalIssueSectionsFiltered}
                                    legalIssues={legalIssues}
                                    termForm={loiForm?.termForm}
                                    displayAsLabels={true}
                                    mode={LoiFormMode.BuyerProfile}
                                />
                            </CardBody>
                        </Col>
                    </Row>
                )}
                {isFF_MrktplcBuyers_3_13Enabled && additionalCovenants.length > 0 && (
                    <Row>
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header space-between">
                                Additional NDA Covenants
                            </CardHeader>
                            <CardBody>
                                <LegalIssuesForm
                                    legalIssueSections={additionalCovenantSections}
                                    legalIssues={additionalCovenants}
                                    termForm={loiForm?.termForm}
                                    displayAsLabels={true}
                                    mode={LoiFormMode.BuyerProfile}
                                />
                            </CardBody>
                        </Col>
                    </Row>
                )}
                {isUploadsExist(MarketplaceBuyerUploadAccessLevel.Public) && (
                    <Row>
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header space-between">
                                Public Documents
                            </CardHeader>
                            <BuyerUploads
                                accessLevel={
                                    MarketplaceBuyerUploadAccessLevel.Public
                                }
                                isReadonly={true}></BuyerUploads>
                        </Col>
                    </Row>
                )}
                {isUploadsExist(MarketplaceBuyerUploadAccessLevel.Private) && (
                    <Row>
                        {' '}
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header space-between">
                                <div className="private-documents-text">
                                    <div>Private Documents</div>
                                    <InfoTooltip
                                        idText="private-documents-tooltip"
                                        text="These only appear to Sellers which have executed a Data NDA with you."
                                    />
                                </div>
                            </CardHeader>
                            <BuyerUploads
                                accessLevel={
                                    MarketplaceBuyerUploadAccessLevel.Private
                                }
                                isReadonly={true}></BuyerUploads>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </div>
    );
};
